const vacancyLists = document.querySelectorAll('[data-campaign-vacancies]');

vacancyLists.forEach(component => {
    if (component.dataset.vacanciesInitialized === 'true') {
        return;
    }

    component.dataset.vacanciesInitialized = 'true';

    const list = component.querySelector('[data-vacancy-list]');
    const toggle = component.querySelector('[data-vacancy-toggle]');
    const sortSelect = component.querySelector('[data-vacancy-sort]');
    const locationSelect = component.querySelector('[data-vacancy-location]');
    const count = component.querySelector('[data-vacancy-count]');
    const empty = component.querySelector('[data-vacancy-empty]');
    const visibleCount = Number.parseInt(component.dataset.visibleCount, 10) || 5;
    let expanded = false;
    let heightAnimation;
    let scrollFrame;
    let expandedBeforePrint;
    let previousOverflowAnchor;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (!list) {
        return;
    }

    list.querySelectorAll('.campaignVacancyList-link').forEach(link => {
        const setAccentOrigin = event => {
            if (event.pointerType === 'touch') return;

            const bounds = link.getBoundingClientRect();
            const origin = event.clientY < bounds.top + bounds.height / 2 ? 'top' : 'bottom';
            link.style.setProperty('--vacancy-accent-origin', origin);
        };

        link.addEventListener('pointerenter', setAccentOrigin);
        link.addEventListener('pointerleave', setAccentOrigin);
    });

    const getItems = () => [...list.querySelectorAll('[data-vacancy-item]')];
    const matchesLocation = item => !locationSelect.value
        || (item.dataset.location ?? '').trim().toLocaleLowerCase('nb') === locationSelect.value.toLocaleLowerCase('nb');

    const updateVisibility = () => {
        let matchingCount = 0;
        getItems().forEach(item => {
            const matches = matchesLocation(item);
            item.hidden = !matches || (!expanded && matchingCount >= visibleCount);
            if (matches) matchingCount += 1;
            item.inert = item.hidden;
            if (item.hidden) {
                item.setAttribute('aria-hidden', 'true');
            } else {
                item.removeAttribute('aria-hidden');
            }
        });
        const countText = (matchingCount === 1 ? count.dataset.singular : count.dataset.plural)
            .replace('{0}', matchingCount);
        if (count.textContent !== countText) count.textContent = countText;
        empty.hidden = matchingCount > 0;

        if (!toggle) {
            return;
        }

        toggle.hidden = matchingCount <= visibleCount;
        toggle.setAttribute('aria-expanded', String(expanded));
        toggle.querySelector('[data-vacancy-toggle-text]').textContent = expanded
            ? toggle.dataset.showFewer
            : toggle.dataset.showMore;
    };

    const cancelAnimation = () => {
        window.cancelAnimationFrame(scrollFrame);
        heightAnimation?.cancel();
        heightAnimation = undefined;
        list.style.removeProperty('height');
        list.classList.remove('campaignVacancyList-list--animating');
        if (previousOverflowAnchor !== undefined) {
            document.documentElement.style.overflowAnchor = previousOverflowAnchor;
            previousOverflowAnchor = undefined;
        }
    };

    const setExpanded = nextExpanded => {
        const startScrollY = window.scrollY;
        const startHeight = list.getBoundingClientRect().height;
        cancelAnimation();
        // The focused toggle moves as the list grows. Disable document anchoring
        // during the animation so the browser does not follow it down the page.
        previousOverflowAnchor = document.documentElement.style.overflowAnchor;
        document.documentElement.style.overflowAnchor = 'none';
        expanded = nextExpanded;
        updateVisibility();
        const targetHeight = list.getBoundingClientRect().height;
        const scrollMargin = Number.parseFloat(window.getComputedStyle(component).scrollMarginBlockStart) || 0;
        const targetScrollY = Math.max(0, startScrollY + component.getBoundingClientRect().top - scrollMargin);

        if (reducedMotion.matches || typeof list.animate !== 'function') {
            window.scrollTo({ top: expanded ? startScrollY : targetScrollY, behavior: 'instant' });
            cancelAnimation();
            return;
        }

        // Keep rows rendered while the viewport shrinks, but make clipped links
        // unavailable to keyboard users and assistive technology immediately.
        getItems().forEach(item => { item.hidden = !matchesLocation(item); });
        list.classList.add('campaignVacancyList-list--animating');
        const animation = list.animate([
            { height: `${startHeight}px` },
            { height: `${targetHeight}px` },
        ], {
            duration: 350,
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
            fill: 'both',
        });
        heightAnimation = animation;
        window.scrollTo({ top: startScrollY, behavior: 'instant' });

        if (!expanded) {
            const scroll = () => {
                if (heightAnimation !== animation) return;

                const progress = animation.effect.getComputedTiming().progress ?? 0;
                window.scrollTo({
                    top: startScrollY + (targetScrollY - startScrollY) * progress,
                    behavior: 'instant',
                });
                if (progress < 1) scrollFrame = window.requestAnimationFrame(scroll);
            };
            scrollFrame = window.requestAnimationFrame(scroll);
        }

        animation.finished.then(() => {
            if (heightAnimation !== animation) return;
            if (!expanded) window.scrollTo({ top: targetScrollY, behavior: 'instant' });
            cancelAnimation();
            updateVisibility();
        }, () => {});
    };

    const compareValues = (first, second, key, direction) => {
        const firstValue = first.dataset[key]?.trim() ?? '';
        const secondValue = second.dataset[key]?.trim() ?? '';

        if (!firstValue && secondValue) return 1;
        if (firstValue && !secondValue) return -1;

        const result = firstValue.localeCompare(secondValue, 'nb', {
            numeric: true,
            sensitivity: 'base',
        });

        if (result !== 0) {
            return direction === 'ascending' ? result : -result;
        }

        return (first.dataset.title ?? '').localeCompare(second.dataset.title ?? '', 'nb', {
            sensitivity: 'base',
        });
    };

    const sortItems = () => {
        const [key, direction] = sortSelect.value.split('-');
        getItems()
            .sort((first, second) => compareValues(first, second, key, direction))
            .forEach(item => list.appendChild(item));
    };

    const updateResults = () => {
        cancelAnimation();
        expanded = false;
        sortItems();
        updateVisibility();
    };
    sortSelect.addEventListener('change', updateResults);
    locationSelect.addEventListener('change', updateResults);

    toggle?.addEventListener('click', () => {
        setExpanded(!expanded);
    });

    const settleAnimation = () => {
        cancelAnimation();
        updateVisibility();
    };
    window.addEventListener('resize', settleAnimation);
    reducedMotion.addEventListener('change', settleAnimation);
    window.addEventListener('beforeprint', () => {
        expandedBeforePrint = expanded;
        expanded = true;
        settleAnimation();
    });
    window.addEventListener('afterprint', () => {
        expanded = expandedBeforePrint ?? expanded;
        settleAnimation();
    });

    updateResults();
});
